
import React from "react";
import { Schemes, Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import * as ReactRouterDom from "react-router-dom";
import { Button, ButtonManageRef, Checkbox, Form, FormControl, Scheme, TextAreaInput, TextInput, useScreenDimension } from "@rivtn/noseur";
import MediaInput, { MediaInputManageRef } from "../../../../shared/MediaInput";
import { ApiResponseData, BaseService, ConsusService, HostService, IHost, IHostBlogCategory, IHostWebsite, IMember, IUploadedFile, SanitizedResponse } from "../../../../../services";

export interface CategoriesProps {
    host: IHost;
    profile: IMember;
    hostWebsite: IHostWebsite;
}

function Categories(props: CategoriesProps) {

    const { host } = props;
    const { width } = useScreenDimension();
    const hostService = HostService.getInstance();
    const consusService = ConsusService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.host.settings.blog.categories;
    const mediaInputManageRef = React.useRef<MediaInputManageRef>(null);
    const [hostWebsite, setHostWebsite] = React.useState<IHostWebsite>(props.hostWebsite);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);
    const [hostBlogCategories, setHostBlogCategories] = React.useState<ApiResponseData<IHostBlogCategory>>({});

    React.useEffect(fetchHostBlogCategories, []);

    return (<Form scheme={Schemes.OMNEHOST} className="profile-settings team-settings" style={{ padding: 0, boxShadow: "none" }}>
        <Checkbox scheme={Schemes.OMNEHOST} label={labels.enable_ads} defaultChecked={hostWebsite.blog_settings.enable_rivtn_ad_on_categories_page} onChecked={(checked) => {
            hostService.updateHostWebsite(host.external_id, { ...hostWebsite, blog_settings: { ...hostWebsite.blog_settings, enable_rivtn_ad_on_categories_page: checked } }).then(({ }: SanitizedResponse<IHostWebsite>) => {
                Siren.alert(labels.update_successful);
            }).catch(BaseService.reportError);
        }} />
        <hr style={{ marginBottom: 0 }} />
        <div className="pagination">
            {Elements.buildPaginator(hostBlogCategories ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchHostBlogCategories),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {(width > 800) && Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchHostBlogCategories), labels.sorts[0].label)}
        </div>
        <div style={{ display: "flex", marginTop: 10, justifyContent: "space-between", flexDirection: (width > 800 ? "row" : "column") }}>
            <Form scheme={Schemes.OMNEHOST} style={{ display: "flex", flexDirection: (width > 800 ? "row" : "column") }}>
                <FormControl isFieldset label={labels.name} style={width > 800 ? { marginLeft: 15, width: 200 } : { width: "100%", marginBottom: 10 }}>
                    <TextInput defaultValue={searchParams.get("name") ?? ""} onInputComplete={(v) => {
                        Utils.updateSearchParams("name", v, setSearchParams, fetchHostBlogCategories);
                    }} />
                </FormControl>
                {(width > 800) && (<FormControl isFieldset label={labels.description} style={{ marginLeft: 15, width: 200 }}>
                    <TextInput defaultValue={searchParams.get("desc") ?? ""} onInputComplete={(v) => {
                        Utils.updateSearchParams("desc", v, setSearchParams, fetchHostBlogCategories);
                    }} />
                </FormControl>)}
            </Form>
            <Button raised scheme={Schemes.OMNEHOST} text={labels.create_category} style={{ marginTop: 10, padding: "10px 35px" }} onClick={() => addOrUpdate()} />
        </div>
        <hr style={{ margin: "20px 0px" }} />
        {!hostBlogCategories.content ? Elements.LOADING_STATE : (<div className="ho-cats">
            {hostBlogCategories.content?.map((hostBlogCategory) => {
                return (<div key={hostBlogCategory.external_id} className="ho-cat" onClick={() => addOrUpdate(hostBlogCategory)}>
                    <img alt={hostBlogCategory.name} src={hostBlogCategory.icon_location} />
                    <div className="details">
                        <span className="t lined-limit">{hostBlogCategory.name}</span>
                        <div className="dets">{hostBlogCategory.description}</div>
                    </div>
                </div>);
            })}
        </div>)}
        {hostBlogCategories.content && !hostBlogCategories.content.length && (Elements.buildEmptyListView("fa fa-circle", (<span>
            {labels.empty_label_parts.no_cat}{" "}
            <span className="link" onClick={() => addOrUpdate()}>{labels.empty_label_parts.create}</span>{" "}
            {labels.empty_label_parts.a_new_cat}
        </span>)))}
    </Form>);

    function fetchHostBlogCategories() {
        setHostBlogCategories({});
        hostService.queryHostBlogCategories(host.external_id, {
            name: searchParams.get("name"),
            sort: searchParams.get("sort") ?? "",
            description: searchParams.get("desc"),
            page: searchParams.get("page") ?? "1",
            size: searchParams.get("size") ?? "100",
        }).then(({ apiResponse }: SanitizedResponse<IHostBlogCategory>) => {
            setHostBlogCategories(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function addOrUpdate(category: IHostBlogCategory = {} as any) {
        const now = new Date().getTime();
        const dialogLabels = labels.dialog.create_update;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };

        const dialog = ConfirmDialog({
            closable: true,
            style: { minWidth: 560, maxWidth: 560 },
            title: (category.external_id ? dialogLabels.update_cat : dialogLabels.create),
            confirmLabel: (category.external_id ? dialogLabels.update : dialogLabels.create),
            footerElements: (category.external_id
                ? (<Button raised scheme={Scheme.DANGER} text={dialogLabels.delete} style={{ padding: "10px 35px" }} onClick={() => {
                    dialog.hide(() => deleteCategory(category));
                }} />)
                : undefined),
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: 20 }}>
                <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
                    <MediaInput noFillTemplate default={category.icon_location ? category.icon_location + "?n=" + now : undefined}
                        manageRef={mediaInputManageRef} style={{ borderRadius: "100%" }} width={50} height={50} maxFileSize={20000000}
                        onValidationFail={(_: File, error: string) => {
                            dialogErrorRef.current!.innerText = error;
                            return true;
                        }} />
                    <FormControl fill isFieldset label={dialogLabels.name} {...Elements.FCFL} style={{ flex: 1, marginLeft: 15 }}>
                        <TextInput defaultValue={category.name} onInputComplete={(v) => {
                            dialogErrorRef.current!.innerText = " ";
                            category.name = v;
                        }} />
                    </FormControl>
                </div>
                <FormControl fill isFieldset label={dialogLabels.description} {...Elements.FCFL} style={{ marginTop: 10 }}>
                    <TextAreaInput fill style={{ height: 180 }} defaultValue={category.description} onInputComplete={(v) => {
                        dialogErrorRef.current!.innerText = " ";
                        category.description = v;
                    }} />
                </FormControl>
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                button?.setLoadingState(true);
                const triggerActualAction = (mediaLocation?: string) => {
                    if (mediaLocation) {
                        category.icon_location = mediaLocation;
                    }
                    const method = (category.external_id ? hostService.updateHostBlogCategory : hostService.createHostBlogCategory).bind(hostService);
                    method(host.external_id, category).then(() => {
                        Siren.alert(category.external_id ? dialogLabels.update_successful : dialogLabels.add_successful);
                        dialog.hide();
                        fetchHostBlogCategories();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                }
                if (files.length) {
                    const file = files[0];
                    const fileName = (category.external_id && category.icon_location ? category.icon_location?.substring(category.icon_location.lastIndexOf("/") + 1) : `${profile.janus_user_external_id}_${file.name?.substring(file.name.length - 10, file.name.length)}`);
                    consusService.uploadFile("/omnehost/host/events/", fileName, file).then(async ({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                        triggerActualAction(sanitized.consus_download_url);
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                } else {
                    if (!category.icon_location) {
                        dialogErrorRef.current!.innerText = dialogLabels.choose_logo;
                        button?.setLoadingState(false);
                        return false;
                    }
                    triggerActualAction();
                }
                return false;
            }
        });
        dialog.show();
    }

    function deleteCategory(category: IHostBlogCategory) {
        const dialogLabels = labels.dialog.delete;
        const dialogErrorRef = { current: null } as any;

        const dialog = ConfirmDialog({
            closable: true,
            style: { maxWidth: 500 },
            title: dialogLabels.title,
            confirmScheme: Scheme.DANGER,
            desc: dialogLabels.desc.replace("${name}", category.name),
            content: (<div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>),
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                dialogErrorRef.current!.innerText = " ";
                const removeFromStore = () => {
                    hostService.deleteHostBlogCategory(host.external_id, category).then(() => {
                        Siren.alert(dialogLabels.successful);
                        dialog.hide();
                        fetchHostBlogCategories();
                    }).catch((error: any) => {
                        dialogErrorRef.current!.innerText = error.errorMessage;
                        BaseService.reportError(error);
                        button?.setLoadingState(false);
                    });
                };
                consusService.deleteFile([category.icon_location]).then(removeFromStore).catch(removeFromStore);
                return false;
            }
        });
        dialog.show();
    }

}

export default Categories;
